/**
 * Gold — a capped counter, off the grid.
 *
 * The third container, beside `inventory.ts`'s packing grid and `belt.ts`'s
 * columns, and the only one with no geometry at all. Gold never occupies a
 * cell: it is one number with a ceiling over it (decided 2026-08-12,
 * `TODO.md` #24, `docs/GAME-DESIGN.md`). That is what makes the death penalty's
 * "lose some gold" a subtraction — `forfeit` — instead of an inventory
 * operation that could fail because a great sword is in the way.
 *
 * ## The cap curve, and what it means in play
 *
 * **`level × 10 000`.** D2's own curve, not an invention: a level-1 character
 * carries 10 000, a level-10 one 100 000, and a level-99 one 990 000. Three
 * sources agree on it — PureDiablo's D2 wiki (https://www.purediablo.com/d2wiki/Gold),
 * the Diablo Wiki's Gold article (https://diablo.fandom.com/wiki/Gold), and a
 * Phrozen Keep thread that locates the literal 10 000 in the 1.13c binary
 * (https://mail.d2mods.info/forum/viewtopic.php?f=8&t=61123). All three also
 * state the part that matters more than the number: gold costs no inventory
 * space and no movement speed.
 *
 * Linear is the right shape here, and it is worth saying why rather than
 * leaning on "D2 did it". With this game's placeholder gold rates — a pile
 * averaging `2 × (10 + 5 × depth)` on about 45% of kills, sixty monsters to a
 * floor (`drops.ts`) — a floor pays roughly `540 + 270 × depth` gold, while the
 * cap at a level keeping pace with depth is `10 000 × depth`. Divide one by the
 * other and the answer barely moves: **the cap is about twelve floors of pure
 * hoarding on floor 1, thirty-one on floor 10, and never more than
 * thirty-seven however deep it gets.** It is not a wall a player hits once at
 * level 1 and never again, and it is not one they only meet at 99 — it is a
 * permanent, mild brake, tightest exactly where a character has least reason to
 * be sitting on a fortune. A flat cap would become the whole game by floor 40;
 * a quadratic one would stop existing after floor 5.
 *
 * What it means at the table: the cap is a **hoarding brake, not a spending
 * constraint**. Nothing the vendor sells or repairs costs anything near it
 * (a broken Full Plate Mail repairs for a quarter of its price), so a player
 * never fails to afford something because of the ceiling. What they do is walk
 * over the last few piles on a floor, which is D2's own texture. And it bounds
 * the death penalty: what "some gold" can cost is proportional to level, so the
 * loss scales with the run instead of with how long since the last town trip.
 *
 * Everything here is whole gold. A fractional coin is a programmer error and
 * throws, like a malformed grid; running out of money is an ordinary outcome
 * and comes back as a result.
 */
/** Gold of carrying capacity per character level. D2's number, cited in the module note. */
export const GOLD_PER_LEVEL = 10_000;
const assertLevel = (level) => {
    if (!Number.isInteger(level) || level < 1) {
        throw new RangeError(`A character level is a positive integer, received ${level}`);
    }
};
const assertGold = (what, amount) => {
    if (!Number.isInteger(amount) || amount < 0) {
        throw new RangeError(`${what} is a whole non-negative amount of gold, received ${amount}`);
    }
};
/** The most gold a character of this level may carry. */
export const goldCapacity = (level) => {
    assertLevel(level);
    return level * GOLD_PER_LEVEL;
};
/** This purse's own ceiling. */
export const goldCapacityOf = (purse) => goldCapacity(purse.level);
/** Whether one more coin would be left on the floor. */
export const isPurseFull = (purse) => purse.carried >= goldCapacityOf(purse);
/**
 * A purse for a character of this level, empty unless a balance is supplied.
 *
 * A balance above the cap throws rather than clamping: it can only come from a
 * corrupt record or a wire message that was not validated, and silently binning
 * a player's gold is the worst of the available answers.
 */
export const createPurse = (level, carried = 0) => {
    assertLevel(level);
    assertGold('A starting balance', carried);
    const capacity = goldCapacity(level);
    if (carried > capacity) {
        throw new RangeError(`A level ${level} purse holds at most ${capacity} gold, received ${carried}`);
    }
    return { carried, level };
};
/** Puts a pile in, up to the cap, and hands back whatever would not fit. */
export const deposit = (purse, amount) => {
    assertGold('A gold pile', amount);
    const stored = Math.min(amount, goldCapacityOf(purse) - purse.carried);
    return {
        purse: stored === 0 ? purse : { ...purse, carried: purse.carried + stored },
        stored,
        overflow: amount - stored,
    };
};
/** Pays a price, or refuses the whole payment. Never pays part of one. */
export const spend = (purse, amount) => {
    assertGold('A price', amount);
    if (amount > purse.carried)
        return { ok: false, reason: 'insufficient-gold' };
    return { ok: true, purse: { ...purse, carried: purse.carried - amount }, spent: amount };
};
/**
 * Takes up to `amount` away, whatever is there — the death penalty's half of
 * this module.
 *
 * Unlike `spend` it cannot refuse: a penalty that declined to apply because the
 * player was poor would reward being poor. It reports what it actually took, so
 * a corpse or a log can say so.
 */
export const forfeit = (purse, amount) => {
    assertGold('A forfeit', amount);
    const lost = Math.min(amount, purse.carried);
    return { purse: lost === 0 ? purse : { ...purse, carried: purse.carried - lost }, lost };
};
/**
 * The same gold at a new character level — a level-up, and the only thing that
 * ever moves the ceiling.
 *
 * A level whose cap could not hold what is already carried throws. Character
 * levels do not go down, so asking for one is a bug in the caller rather than a
 * situation the game can be in, and clamping would destroy gold to hide it.
 */
export const atLevel = (purse, level) => createPurse(level, purse.carried);
//# sourceMappingURL=gold.js.map